import React, { FC, useEffect, useReducer, useRef } from 'react'
import { useCarousel } from 'hooks/carousel'
import GalleryConnector, { ConnectorProps as GalleryProps } from '../../connectors/Gallery'
import MobileARExperience2 from '../ARExperience/Mobile2'
import MetaInfo from '../Products/MetaInfo'
import Gallery from './Gallery'
import GalleryModal from './Modal'
import Thumbnails from './Thumbnails'
import { GalleryLayout } from './styles'
import {
  GALLERY_ITEM_TYPE_ARTIST_VIDEO,
  GALLERY_ITEM_TYPE_VIEW_IN_A_ROOM_IMAGE,
} from '../../constants'
import useIsMobile from 'hooks/useIsMobile'
import { BROWSER_MIN_WIDTH_DESKTOP } from '../../selectors/gallery'
import { ARTWORK_CATEGORIES } from 'components/SA/Polaroid/helpers'

type State = {
  currentItem: number
  rendered: boolean
  eventsAvailable: boolean
  showModal: boolean
  carouselIndexOfViewInARoomItem: number | null
}
const initialState: State = {
  currentItem: 0,
  rendered: true,
  eventsAvailable: false,
  showModal: false,
  carouselIndexOfViewInARoomItem: null,
}

type Action = Partial<State>

const reducer = (state: State, action: Action) => ({ ...state, ...action })

const Component: FC<GalleryProps> = (props) => {
  const {
    artworkId,
    artworkCategory,
    currentTab,
    galleryDimensions,
    isAdmin,
    updateDataLayer,
    selectedProduct,
    selectedFrameOption,
  } = props
  const { material } = selectedProduct

  const isMobile = useIsMobile(BROWSER_MIN_WIDTH_DESKTOP)
  const galleryItems = props.galleryItems.filter(
    (galleryItem) =>
      // artist videos are desktop only for now due to some weird mobile bugs
      !(isMobile && galleryItem.galleryItemType === GALLERY_ITEM_TYPE_ARTIST_VIDEO) &&
      // sculptures should not have view in a room
      !(
        artworkCategory === ARTWORK_CATEGORIES.SCULPTURE &&
        galleryItem.galleryItemType === GALLERY_ITEM_TYPE_VIEW_IN_A_ROOM_IMAGE
      )
  )

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    eventsAvailable: false,
    // This attributes is used for datalayer ( GTM events )
    rendered: false,
    showModal: false,
  })
  const { eventsAvailable, rendered, showModal, carouselIndexOfViewInARoomItem } = state
  const carouselHook = useCarousel(galleryItems.length) //  next, prev,

  const { index: currentCarouselIndex, setIndex: setCurrentCarouselIndex } = carouselHook
  const galleryRef = useRef<Record<string, HTMLDivElement | null>>({})
  useEffect(() => {
    setCurrentCarouselIndex(0)
  }, [material])
  useEffect(() => {
    // Prevent events firing on INITIAL load
    if (!eventsAvailable) {
      dispatch({
        eventsAvailable: true,
      }) // Enable Events

      return
    }

    // Any time the image changes we need to fire a GTM event
    const isVIR = currentCarouselIndex === carouselIndexOfViewInARoomItem
    updateDataLayer({
      event: isVIR ? 'multiple images - view in a room' : 'multiple images - artwork',
    })
  }, [currentCarouselIndex])
  useEffect(() => {
    // First Ever Rendering of a page - handle mobile
    if (!rendered) {
      // Lets handle VIEW IN ROOM query
      const viewInRoomQueryStringParam = window.location.search.search(/vir=/) > -1
      const viewInARoomPosition = galleryItems.findIndex(
        (item) => item.galleryItemType === GALLERY_ITEM_TYPE_VIEW_IN_A_ROOM_IMAGE
      )

      if (viewInARoomPosition > -1) {
        dispatch({ carouselIndexOfViewInARoomItem: viewInARoomPosition })
      }

      if (viewInRoomQueryStringParam) {
        setCurrentCarouselIndex(viewInARoomPosition || 0)
        updateDataLayer({
          event: 'toaf view in room query',
        })
      }
    } else {
      dispatch({
        eventsAvailable: false,
      }) // Prevent Events on NEW Product

      setCurrentCarouselIndex(0)
    }

    // Only needs to fire ONCE
    if (!rendered) {
      dispatch({
        rendered: true,
      })
    } // Update Reset Logic on SKU change
  }, [currentTab, artworkId])

  // Handle switching between TABS/PRODUCTS and new image counts
  if (!galleryItems[currentCarouselIndex]) {
    return null
  }

  return (
    <>
      <GalleryLayout data-section='gallery-content'>
        <Thumbnails
          clickThumbnail={setCurrentCarouselIndex}
          currentItem={currentCarouselIndex}
          rendered={rendered}
        />

        <Gallery
          currentTab={currentTab}
          dimensions={galleryDimensions}
          galleryRef={galleryRef}
          galleryItems={galleryItems}
          isAdmin={isAdmin}
          carouselHook={carouselHook}
          clickImage={() => {
            if (galleryItems.length > 1) {
              dispatch({
                showModal: true,
              })
              updateDataLayer({
                event: 'view fullscreen',
              })
            }
          }}
          updateDataLayer={updateDataLayer}
          selectedProduct={selectedProduct}
          selectedFrameOption={selectedFrameOption}
        />

        <div data-section='mobile-actions'>
          <MetaInfo inGallery />

          <MobileARExperience2 />
        </div>
      </GalleryLayout>

      <GalleryModal
        closeModal={() => {
          dispatch({
            showModal: false,
          })
          updateDataLayer({
            event: 'close fullscreen',
          })
        }}
        startPosition={currentCarouselIndex}
        showModal={showModal}
      />
    </>
  )
}

export default GalleryConnector(Component)
